// src/modules/Admin/components/AdminLayout.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../config/supabase';
import {
  FaHome,
  FaNewspaper,
  FaUsers,
  FaChartBar,
  FaCog,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaArrowLeft,
  FaBell,
  FaGlobe,
  FaBriefcase,
  FaUsersCog,
  FaBook,
  FaUserTie,
  FaBullseye,
  FaReceipt,
  FaHandshake,
  FaBoxOpen,
  FaShoppingCart
} from 'react-icons/fa';

import logoVega from '../../../assets/logovega.svg';

// Styled Components
const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f5f5f5;
`;

const Sidebar = styled.aside`
  width: 260px;
  background: #111;
  color: white;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 100;
  transition: transform 0.3s ease;
  overflow: hidden;
  clip-path: inset(0 0 0 0);

  @media (max-width: 1024px) {
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;

  @media (max-width: 1024px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const SidebarHeader = styled.div`
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  max-width: 100%;
  overflow: hidden;

  img {
    height: 28px;
    flex-shrink: 0;
  }
`;

const LogoBadge = styled.span`
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  font-size: 10px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const NavSection = styled.nav`
  flex: 1;
  padding: 20px 12px;
  overflow-y: auto;
`;

const NavGroup = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const NavGroupTitle = styled.h3`
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 0 12px;
  margin-bottom: 8px;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.6)'};
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 200ms;

  svg {
    font-size: 18px;
  }

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const UserSection = styled.div`
  padding: 16px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 12px;
`;

const UserAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserDetails = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-size: 13px;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .email {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  min-height: 100vh;

  @media (max-width: 1024px) {
    margin-left: 0;
  }
`;

const TopBar = styled.header`
  background: white;
  border-bottom: 1px solid #e5e5e5;
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 20px;
  color: #333;
  cursor: pointer;
  padding: 8px;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const BackToSiteLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  text-decoration: none;
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const PageContent = styled.div`
  padding: 24px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const NotificationButton = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$hasNew ? 'rgba(239, 68, 68, 0.1)' : '#f5f5f5'};
  color: ${props => props.$hasNew ? '#ef4444' : '#666'};
  text-decoration: none;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$hasNew ? 'rgba(239, 68, 68, 0.15)' : '#e5e5e5'};
    color: ${props => props.$hasNew ? '#dc2626' : '#333'};
  }

  svg {
    font-size: 18px;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 700;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
`;

const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const { user, signOut, hasPermission, userRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Buscar contagem de leads novos
  useEffect(() => {
    fetchNewLeadsCount();

    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchNewLeadsCount, 30000);

    // Inscrever para atualizações em tempo real
    const subscription = supabase
      .channel('leads-changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'leads'
      }, () => {
        fetchNewLeadsCount();
      })
      .subscribe();

    return () => {
      clearInterval(interval);
      subscription.unsubscribe();
    };
  }, []);

  const fetchNewLeadsCount = async () => {
    try {
      const { count, error } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');

      if (!error && count !== null) {
        setNewLeadsCount(count);
      }
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Menu items com permissões
  const allMenuItems = [
    { path: '/admin', icon: FaHome, label: 'Dashboard', exact: true },
    { path: '/admin/meu-manual', icon: FaBook, label: 'Meu Manual' },
    { path: '/admin/blog', icon: FaNewspaper, label: 'Blog', permission: 'blog.view' },
    { path: '/admin/vagas', icon: FaBriefcase, label: 'Vagas', permission: 'jobs.view' },
    { path: '/admin/leads', icon: FaUsers, label: 'Leads', permission: 'leads.view' },
    { path: '/admin/produtos', icon: FaBoxOpen, label: 'Produtos', permission: 'settings.view' },
    { path: '/admin/pedidos', icon: FaShoppingCart, label: 'Pedidos', permission: 'settings.view' },
    { path: '/admin/analytics', icon: FaChartBar, label: 'Analytics', permission: 'analytics.view' },
    { path: '/admin/inteligencia', icon: FaGlobe, label: 'Inteligência', permission: 'market_intelligence.view' },
    { path: '/admin/metas', icon: FaBullseye, label: 'Metas CRM', permission: 'settings.view' },
    { path: '/admin/metas-investidores', icon: FaHandshake, label: 'Metas Investidores', permission: 'settings.view' },
    { path: '/admin/gastos', icon: FaReceipt, label: 'Declaracao Gastos', permission: 'settings.view' },
    { path: '/admin/investidores', icon: FaUserTie, label: 'Investidores', permission: 'users.view' },
    { path: '/admin/usuarios', icon: FaUsersCog, label: 'Usuarios', permission: 'users.view' },
    { path: '/admin/configuracoes', icon: FaCog, label: 'Configuracoes', permission: 'settings.view' },
  ];

  // Filtrar itens baseado em permissões
  const menuItems = allMenuItems.filter(item => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  return (
    <LayoutContainer>
      <SidebarOverlay $isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(false)} />

      <Sidebar $isOpen={isSidebarOpen}>
        <SidebarHeader>
          <LogoContainer to="/admin">
            <img src={logoVega} alt="Vega Robotics" />
            <LogoBadge>Admin</LogoBadge>
          </LogoContainer>
          <CloseButton onClick={() => setIsSidebarOpen(false)}>
            <FaTimes />
          </CloseButton>
        </SidebarHeader>

        <NavSection>
          <NavGroup>
            <NavGroupTitle>Menu Principal</NavGroupTitle>
            {menuItems.map((item) => (
              <NavItem
                key={item.path}
                to={item.path}
                $active={item.exact ? location.pathname === item.path : isActive(item.path)}
                onClick={() => setIsSidebarOpen(false)}
              >
                <item.icon />
                {item.label}
              </NavItem>
            ))}
          </NavGroup>
        </NavSection>

        <UserSection>
          <UserInfo>
            <UserAvatar
              src={user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.user_metadata?.full_name || user?.email || 'U')}&background=333&color=fff`}
              alt="Avatar"
            />
            <UserDetails>
              <div className="name">{user?.user_metadata?.full_name || 'Usuário'}</div>
              <div className="email">{user?.email}</div>
            </UserDetails>
          </UserInfo>
          <LogoutButton onClick={handleLogout}>
            <FaSignOutAlt />
            Sair
          </LogoutButton>
        </UserSection>
      </Sidebar>

      <MainContent>
        <TopBar>
          <TopBarLeft>
            <MenuButton onClick={() => setIsSidebarOpen(true)}>
              <FaBars />
            </MenuButton>
            <BackToSiteLink to="/">
              <FaArrowLeft />
              Voltar ao site
            </BackToSiteLink>
          </TopBarLeft>
          <TopBarRight>
            <NotificationButton to="/admin/leads" $hasNew={newLeadsCount > 0} title={`${newLeadsCount} lead(s) novo(s)`}>
              <FaBell />
              {newLeadsCount > 0 && (
                <NotificationBadge>{newLeadsCount > 99 ? '99+' : newLeadsCount}</NotificationBadge>
              )}
            </NotificationButton>
          </TopBarRight>
        </TopBar>

        <PageContent>
          <Outlet />
        </PageContent>
      </MainContent>
    </LayoutContainer>
  );
};

export default AdminLayout;
